import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { createTimeslot, deleteTimeslot, Profile, Recipient } from "@/lib/forward-api";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from 'react-i18next';

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const DAY_LABELS: Record<string, string> = {
  MONDAY: "Mo",
  TUESDAY: "Di",
  WEDNESDAY: "Mi",
  THURSDAY: "Do",
  FRIDAY: "Fr",
  SATURDAY: "Sa",
  SUNDAY: "So"
};
const DAY_LABELS_FULL: Record<string, string> = {
  MONDAY: "Montag",
  TUESDAY: "Dienstag",
  WEDNESDAY: "Mittwoch",
  THURSDAY: "Donnerstag",
  FRIDAY: "Freitag",
  SATURDAY: "Samstag",
  SUNDAY: "Sonntag"
};

interface ProfileTimesTabProps {
  profile: Profile;
  recipients: Recipient[];
  onUpdate: () => void;
}

export function ProfileTimesTab({ profile, recipients, onUpdate }: ProfileTimesTabProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [addingForDay, setAddingForDay] = useState<string | null>(null);
  const [newTimeslot, setNewTimeslot] = useState({
    recipientId: recipients[0]?.id || "",
    startTime: "09:00",
    endTime: "17:00",
  });
  const [loading, setLoading] = useState(false);

  const getTimeslotsForDay = (day: string) => {
    if (!profile.timeslots || !profile.timeslots[day]) return [];
    return profile.timeslots[day];
  };

  const getRecipientName = (recipientId: string) => {
    const recipient = recipients.find((r) => r.id === recipientId);
    return recipient?.title || "Unknown";
  };

  const handleAddTime = (day: string) => {
    setAddingForDay(day);
    const defaultRecipient = recipients.find(r => profile.receivers?.includes(r.id));
    setNewTimeslot({ recipientId: defaultRecipient?.id || recipients[0]?.id || "", startTime: "09:00", endTime: "17:00" });
  };

  const handleSaveTimeslot = async () => {
    if (!addingForDay || !newTimeslot.recipientId) return;
    if (newTimeslot.startTime >= newTimeslot.endTime) {
      toast({ title: t('error.title'), description: t('forward.times.startBeforeEnd'), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await createTimeslot(profile.id, {
        receiverId: newTimeslot.recipientId,
        dayOfWeek: addingForDay,
        startTime: newTimeslot.startTime,
        endTime: newTimeslot.endTime,
      });
      toast({ title: t('success.title'), description: t('forward.times.timeslotAdded') });
      setAddingForDay(null);
      onUpdate();
    } catch (error) {
      toast({ title: t('error.title'), description: t('forward.times.timeslotAddError'), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeslot = async (timeslotId: string) => {
    try {
      await deleteTimeslot(profile.id, timeslotId);
      toast({ title: t('success.title'), description: t('forward.times.timeslotRemoved') });
      onUpdate();
    } catch (error) {
      toast({ title: t('error.title'), description: t('forward.times.timeslotRemoveError'), variant: "destructive" });
    }
  };

  const availableRecipients = profile.receivers && profile.receivers.length > 0
    ? recipients.filter(r => profile.receivers.includes(r.id))
    : recipients;

  return (
    <div className="space-y-4">
      {/* Week View - Horizontal Calendar Layout */}
      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((day) => {
          const dayTimeslots = getTimeslotsForDay(day);
          const hasSlots = dayTimeslots.length > 0;

          return (
            <div
              key={day}
              className={`bg-white border rounded-lg overflow-hidden min-h-[200px] flex flex-col ${hasSlots ? 'border-gray-200' : 'border-gray-100'}`}
            >
              {/* Day Header */}
              <div className={`px-3 py-2 text-center border-b ${hasSlots ? 'bg-gray-50' : 'bg-gray-50/50'}`}>
                <span className={`text-sm font-semibold ${hasSlots ? 'text-gray-900' : 'text-gray-400'}`}>
                  {DAY_LABELS[day]}
                </span>
              </div>

              {/* Timeslots */}
              <div className="flex-1 p-2 space-y-1.5">
                {dayTimeslots.length > 0 ? (
                  dayTimeslots.map((slot) => (
                    <div
                      key={slot.id}
                      className="group relative bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded px-2 py-1.5 transition-colors"
                    >
                      <div className="text-xs font-medium text-gray-700 truncate">
                        {getRecipientName(slot.receiverId)}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {slot.startTime} - {slot.endTime}
                      </div>
                      <button
                        onClick={() => handleDeleteTimeslot(slot.id)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <span className="text-xs text-gray-300">–</span>
                  </div>
                )}
              </div>

              {/* Add Button */}
              <div className="p-1.5 border-t border-gray-100">
                <button
                  onClick={() => handleAddTime(day)}
                  className="w-full flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded py-1.5 transition-colors"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {/* Add Timeslot Dialog */}
      <Dialog open={!!addingForDay} onOpenChange={() => setAddingForDay(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('forward.times.addTimeslotFor', { day: addingForDay && DAY_LABELS_FULL[addingForDay] })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('sidebar.recipients')}</Label>
              <Select
                value={newTimeslot.recipientId}
                onValueChange={(value) => setNewTimeslot({ ...newTimeslot, recipientId: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRecipients.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('forward.times.startTime')}</Label>
                <Input
                  type="time"
                  value={newTimeslot.startTime}
                  onChange={(e) => setNewTimeslot({ ...newTimeslot, startTime: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('forward.times.endTime')}</Label>
                <Input
                  type="time"
                  value={newTimeslot.endTime}
                  onChange={(e) => setNewTimeslot({ ...newTimeslot, endTime: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddingForDay(null)}>
                {t('global.button.cancel')}
              </Button>
              <Button onClick={handleSaveTimeslot} disabled={loading || !newTimeslot.recipientId}>
                {loading ? t('forward.times.adding') : t('forward.times.add')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
